import React, { useState, useRef, useEffect } from 'react';
import { Edit2, Download, Copy, ArrowLeft, Trash2, ArrowUpCircle } from 'lucide-react';
import jsPDF from 'jspdf';
import { JobberService } from '../services/jobberService';

// When quote has structures, show per-structure; else treat lineItems as single "Main" block
function getDisplayStructures(quote) {
  if (quote?.structures?.length > 0) return quote.structures;
  return [{ name: 'Main', lineItems: quote?.lineItems || [] }];
}

// Per-structure summary rows: Exterior, Interior, then one row per separate window type (aggregated)
function getStructureSummaryRows(structure, windowTypes = [], settings = {}) {
  const lineItems = structure?.lineItems || [];
  const exteriorPct = (settings.exteriorPercentage ?? 60) / 100;
  const interiorPct = (settings.interiorPercentage ?? 40) / 100;
  const showExterior = settings.showExterior !== false;
  const showInterior = settings.showInterior !== false;

  const exteriorInteriorItems = lineItems.filter(item => {
    const wt = windowTypes.find(w => w.name === item.windowType);
    return wt?.category === 'exterior_interior';
  });
  const separateItems = lineItems.filter(item => {
    const wt = windowTypes.find(w => w.name === item.windowType);
    return wt?.category === 'separate';
  });

  const exteriorInteriorSubtotal = exteriorInteriorItems.reduce((s, i) => s + i.price, 0);
  const exteriorTotal = showExterior ? exteriorInteriorSubtotal * exteriorPct : 0;
  const interiorTotal = showInterior ? exteriorInteriorSubtotal * interiorPct : 0;

  const rows = [];
  if (showExterior && exteriorTotal > 0) {
    rows.push({ label: 'Exterior', quantity: '-', price: exteriorTotal });
  }
  if (showInterior && interiorTotal > 0) {
    rows.push({ label: 'Interior', quantity: '-', price: interiorTotal });
  }
  const separateByType = separateItems.reduce((acc, item) => {
    const name = item.windowType;
    if (!acc[name]) acc[name] = { label: name, quantity: 0, price: 0 };
    acc[name].quantity += item.totalCount || 0;
    acc[name].price += item.price;
    return acc;
  }, {});
  Object.values(separateByType).forEach(row => rows.push(row));

  return rows;
}

const QuoteDetail = ({ quote, onEdit, onBack, onCreateSimilar, quoteNumber, onDelete, windowTypes = [], currentUser }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isJobberConnected, setIsJobberConnected] = useState(false);
  const [showJobberSuccessToast, setShowJobberSuccessToast] = useState(false);
  const [isSendingToJobber, setIsSendingToJobber] = useState(false);
  const menuRef = useRef(null);
  const displayStructures = getDisplayStructures(quote);
  const lineItemsForPdf = quote?.lineItems ?? quote?.structures?.flatMap(s => s.lineItems) ?? [];
  const settings = quote?.settings || {};

  // Check Jobber connection status
  useEffect(() => {
    setIsJobberConnected(JobberService.isAuthenticated());
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) {
      // If no date provided, use today's date
      return new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      // If invalid date, use today's date
      return new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSendToJobber = async () => {
    if (!isJobberConnected || !currentUser) {
      return;
    }

    setIsSendingToJobber(true);
    try {
      // Prepare quote data for Jobber sync
      const quoteForSync = {
        ...quote,
        userId: currentUser.uid
      };

      await JobberService.syncQuoteToJobber(quoteForSync);
      
      // Show success toast
      setShowJobberSuccessToast(true);
      setTimeout(() => {
        setShowJobberSuccessToast(false);
      }, 2500);
    } catch (error) {
      console.error('Failed to send quote to Jobber:', error);
      alert(`Failed to send quote to Jobber: ${error.message}`);
    } finally {
      setIsSendingToJobber(false);
    }
  };

  const handleDownload = () => {
    try {
      // Create a new PDF document
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 20;

      // Helper function to add text with line breaks
      const addText = (text, x, y, options = {}) => {
        const { fontSize = 12, fontStyle = 'normal', color = '#000000', align = 'left' } = options;
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', fontStyle);
        doc.setTextColor(color);
        doc.text(text, x, y, { align });
      };

      // Helper function to add line
      const addLine = (x1, y1, x2, y2) => {
        doc.setDrawColor(200, 200, 200);
        doc.line(x1, y1, x2, y2);
      };

      // Header - Quote Title
      const customerName = quote.customer.name || 'Customer';
      addText(`Quote for ${customerName}`, pageWidth / 2, yPosition, { fontSize: 20, fontStyle: 'bold', align: 'center' });
      yPosition += 15;

      // Customer Information (Top Left)
      addText('Customer Information:', 20, yPosition, { fontSize: 14, fontStyle: 'bold' });
      yPosition += 8;
      
      if (quote.customer.name) {
        addText(quote.customer.name, 20, yPosition, { fontSize: 12, fontStyle: 'bold' });
        yPosition += 6;
      }
      if (quote.customer.address) {
        addText(quote.customer.address, 20, yPosition, { fontSize: 10 });
        yPosition += 5;
      }
      if (quote.customer.phone) {
        addText(quote.customer.phone, 20, yPosition, { fontSize: 10 });
        yPosition += 5;
      }
      if (quote.customer.email) {
        addText(quote.customer.email, 20, yPosition, { fontSize: 10 });
        yPosition += 5;
      }
      addText(`Customer Type: ${quote.customer.type}`, 20, yPosition, { fontSize: 10, color: '#666666' });
      yPosition += 10;

      // Quote Information (Top Right)
      const rightX = pageWidth - 20;
      addText(`Quote #${quoteNumber}`, rightX, 35, { fontSize: 12, fontStyle: 'bold', align: 'right' });
      addText(formatDate(quote.createdAt), rightX, 42, { fontSize: 10, align: 'right' });

      // Line separator
      addLine(20, yPosition, pageWidth - 20, yPosition);
      yPosition += 10;

      // Window Details Section
      addText('Window Details', 20, yPosition, { fontSize: 14, fontStyle: 'bold' });
      yPosition += 10;

      if (lineItemsForPdf.length > 0) {
        // Table header
        addText('Window Type', 20, yPosition, { fontSize: 10, fontStyle: 'bold' });
        addText('Quantity', pageWidth / 2, yPosition, { fontSize: 10, fontStyle: 'bold', align: 'center' });
        addText('Total Price', rightX, yPosition, { fontSize: 10, fontStyle: 'bold', align: 'right' });
        yPosition += 5;

        // Header line
        addLine(20, yPosition, pageWidth - 20, yPosition);
        yPosition += 8;

        // Table rows (flattened for PDF)
        lineItemsForPdf.forEach((item, index) => {
          if (yPosition > pageHeight - 40) {
            doc.addPage();
            yPosition = 20;
          }

          addText(item.windowType, 20, yPosition, { fontSize: 10 });
          addText(item.totalCount.toString(), pageWidth / 2, yPosition, { fontSize: 10, align: 'center' });
          addText(`$${item.price.toFixed(2)}`, rightX, yPosition, { fontSize: 10, align: 'right' });
          yPosition += 6;

          // Row separator line
          if (index < lineItemsForPdf.length - 1) {
            addLine(20, yPosition, pageWidth - 20, yPosition);
            yPosition += 3;
          }
        });

        yPosition += 10;
      } else {
        addText('No window items added to this quote.', 20, yPosition, { fontSize: 10, color: '#666666' });
        yPosition += 10;
      }

      // Quote Summary Section
      addLine(20, yPosition, pageWidth - 20, yPosition);
      yPosition += 10;

      addText('Quote Summary', 20, yPosition, { fontSize: 14, fontStyle: 'bold' });
      yPosition += 10;

      // Summary items
      if (quote.settings.showExterior) {
        addText('Exterior Windows', 20, yPosition, { fontSize: 10 });
        addText(`$${quote.totals.exteriorTotal.toFixed(2)}`, rightX, yPosition, { fontSize: 10, align: 'right' });
        yPosition += 6;
      }

      if (quote.settings.showInterior) {
        addText('Interior Windows', 20, yPosition, { fontSize: 10 });
        addText(`$${quote.totals.interiorTotal.toFixed(2)}`, rightX, yPosition, { fontSize: 10, align: 'right' });
        yPosition += 6;
      }

      if (quote.totals.separateItems && quote.totals.separateItems.length > 0) {
        quote.totals.separateItems.forEach(item => {
          addText(item.windowType, 20, yPosition, { fontSize: 10 });
          addText(`$${item.price.toFixed(2)}`, rightX, yPosition, { fontSize: 10, align: 'right' });
          yPosition += 6;
        });
      }

      // Total line
      yPosition += 5;
      addLine(20, yPosition, pageWidth - 20, yPosition);
      yPosition += 8;

      addText('Total Amount', 20, yPosition, { fontSize: 12, fontStyle: 'bold' });
      addText(`$${quote.totals.total.toFixed(2)}`, rightX, yPosition, { fontSize: 12, fontStyle: 'bold', align: 'right' });

      // Footer
      const footerY = pageHeight - 20;
      addText('Thank you for your business!', pageWidth / 2, footerY, { fontSize: 10, align: 'center', color: '#666666' });

      // Generate PDF blob for mobile compatibility
      const pdfBlob = doc.output('blob');
      const fileName = `quote-${quoteNumber}-${quote.customer.name ? quote.customer.name.replace(/\s+/g, '-') : 'customer'}.pdf`;
      
      // Try multiple download methods for better mobile compatibility
      try {
        // Method 1: Direct download (works on most browsers)
        doc.save(fileName);
      } catch (error) {
        console.log('Direct download failed, trying blob method:', error);
        
        // Method 2: Blob URL method (better for mobile)
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.style.display = 'none';
        
        // Add to DOM, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the URL
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
      
    } catch (error) {
      console.error('PDF generation failed:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-primary">
      {/* Header with Navigation */}
      <div className="header shadow-lg border-b border-primary sticky top-0 z-10">
        {/* Header Content - Single Row Layout */}
        <div className="flex items-center justify-between p-4 w-full relative">
          {/* Left: Back Button - Icon Only */}
          <button 
            onClick={onBack} 
            className="flex items-center justify-center text-accent-primary hover:text-accent-hover flex-shrink-0 touchable"
            aria-label="Back to Quotes"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          {/* Center: Title - Two Lines */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
            <div className="text-sm md:text-base text-primary font-normal">quote for</div>
            <div className="text-lg md:text-2xl font-bold text-primary mt-1">
              {quote.customer.name || 'Customer'}
            </div>
          </div>
          
          {/* Right: Hamburger Menu */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Hamburger Menu */}
            <div className={`hamburger-menu ${isMenuOpen ? 'open' : ''}`} ref={menuRef}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="hamburger-button touchable"
                aria-label="Menu"
              >
                <div className="hamburger-line"></div>
                <div className="hamburger-line"></div>
                <div className="hamburger-line"></div>
              </button>
              
              <div className={`hamburger-dropdown ${isMenuOpen ? '' : 'hidden'}`}>
                <button
                  onClick={() => {
                    if (isJobberConnected && !isSendingToJobber) {
                      handleSendToJobber();
                    }
                    setIsMenuOpen(false);
                  }}
                  disabled={!isJobberConnected || isSendingToJobber}
                  className={`hamburger-item touchable ${!isJobberConnected ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={!isJobberConnected ? 'Jobber not connected' : ''}
                >
                  <ArrowUpCircle className="w-4 h-4" />
                  <div className="flex flex-col items-start">
                    <span>Send to Jobber</span>
                    {!isJobberConnected && (
                      <span className="text-xs text-tertiary mt-1">Jobber not connected</span>
                    )}
                    {isSendingToJobber && (
                      <span className="text-xs text-tertiary mt-1">Sending...</span>
                    )}
                  </div>
                </button>
                <button
                  onClick={() => {
                    onCreateSimilar(quote);
                    setIsMenuOpen(false);
                  }}
                  className="hamburger-item touchable"
                >
                  <Copy className="w-4 h-4" />
                  Create Similar
                </button>
                <button
                  onClick={() => {
                    handleDownload();
                    setIsMenuOpen(false);
                  }}
                  className="hamburger-item touchable"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                <button
                  onClick={() => {
                    onEdit(quote);
                    setIsMenuOpen(false);
                  }}
                  className="hamburger-item touchable"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit Quote
                </button>
                <button
                  onClick={() => {
                    onDelete(quote.id);
                    setIsMenuOpen(false);
                  }}
                  className="hamburger-item touchable text-danger"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Quote
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Paper-style Quote Document */}
      <div className="max-w-4xl mx-auto p-3 md:p-6">
        <div className="bg-white shadow-lg rounded-lg p-4 md:p-8 min-h-[11in]">
          {/* Header Section - Customer Info (Top Left) and Quote Info (Top Right) */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-6 md:mb-8">
            {/* Customer Information - Top Left */}
            <div className="flex-1 mb-4 md:mb-0">
              <h2 className="text-xl md:text-2xl font-bold text-black mb-4">Window Quote</h2>
              <div className="text-black text-sm leading-relaxed">
                {quote.customer.name && <p className="font-semibold">{quote.customer.name}</p>}
                {quote.customer.address && <p>{quote.customer.address}</p>}
                {quote.customer.phone && <p>{quote.customer.phone}</p>}
                {quote.customer.email && <p>{quote.customer.email}</p>}
                <p className="mt-2 text-xs text-gray-500">Customer Type: {quote.customer.type}</p>
              </div>
            </div>
            
            {/* Quote Number and Date - Top Right */}
            <div className="text-left md:text-right">
              <p className="text-black text-sm">Quote #{quoteNumber}</p>
              <p className="text-gray-500 text-sm">{formatDate(quote.createdAt)}</p>
            </div>
          </div>

          {/* Line Items Table (per structure when present) */}
          <div className="mb-6 md:mb-8">
            <h3 className="text-lg font-bold text-black mb-4">Window Details</h3>
            {displayStructures.some(s => (s.lineItems || []).length > 0) ? (
              <>
                {displayStructures.map((structure, sIdx) => (
                  <div key={structure.id || sIdx} className="mb-6">
                    {displayStructures.length > 1 && (
                      <h4 className="text-base font-semibold text-black mb-2">{structure.name}</h4>
                    )}
                    {(() => {
                      const summaryRows = getStructureSummaryRows(structure, windowTypes, settings);
                      if (summaryRows.length === 0) return null;
                      const structureTotal = summaryRows.reduce((s, r) => s + r.price, 0);
                      return (
                        <>
                          <div className="hidden md:block border border-gray-400 rounded-sm overflow-hidden">
                            <div className="grid grid-cols-3 bg-gray-100 border-b-2 border-gray-400">
                              <div className="px-4 py-3 font-semibold text-sm text-gray-800 border-r border-gray-400">{structure.name}</div>
                              <div className="px-4 py-3 font-semibold text-sm text-gray-800 text-center border-r border-gray-400" />
                              <div className="px-4 py-3 font-semibold text-sm text-gray-800 text-right">Total ${structureTotal.toFixed(2)}</div>
                            </div>
                            {summaryRows.map((row, index) => (
                              <div key={`${row.label}-${index}`} className="grid grid-cols-3 border-b border-gray-300 last:border-b-0">
                                <div className="px-4 py-3 text-sm text-black border-r border-gray-300">{row.label}</div>
                                <div className="px-4 py-3 text-sm text-gray-700 text-center border-r border-gray-300">{row.quantity === '-' ? '' : row.quantity}</div>
                                <div className="px-4 py-3 text-sm font-semibold text-black text-right">${row.price.toFixed(2)}</div>
                              </div>
                            ))}
                          </div>
                          <div className="block md:hidden border border-gray-400 rounded-sm overflow-hidden">
                            <div className="grid grid-cols-3 bg-gray-100 border-b-2 border-gray-400">
                              <div className="px-3 py-2 font-semibold text-xs text-gray-800 border-r border-gray-400">{structure.name}</div>
                              <div className="px-3 py-2 font-semibold text-xs text-gray-800 text-center border-r border-gray-400" />
                              <div className="px-3 py-2 font-semibold text-xs text-gray-800 text-right">Total ${structureTotal.toFixed(2)}</div>
                            </div>
                            {summaryRows.map((row, index) => (
                              <div key={`${row.label}-${index}`} className="grid grid-cols-3 border-b border-gray-300 last:border-b-0">
                                <div className="px-3 py-2 text-xs text-black border-r border-gray-300">{row.label}</div>
                                <div className="px-3 py-2 text-xs text-gray-700 text-center border-r border-gray-300">{row.quantity === '-' ? '' : row.quantity}</div>
                                <div className="px-3 py-2 text-xs font-semibold text-black text-right">${row.price.toFixed(2)}</div>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                ))}
              </>
            ) : (
              <p className="text-gray-500 text-center py-8">No window items added to this quote.</p>
            )}
          </div>

          {/* Quote Summary - Bottom */}
          <div className="mt-auto">
            <div className="border-t-2 border-gray-300 pt-4">
              <div className="space-y-2">
                {quote.settings.showExterior && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-black">Exterior Windows</span>
                    <span className="text-black font-semibold">
                      ${quote.totals.exteriorTotal.toFixed(2)}
                    </span>
                  </div>
                )}
                {quote.settings.showInterior && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-black">Interior Windows</span>
                    <span className="text-black font-semibold">
                      ${quote.totals.interiorTotal.toFixed(2)}
                    </span>
                  </div>
                )}
                {(() => {
                  const separateItems = quote.totals?.separateItems || [];
                  const byType = separateItems.reduce((acc, item) => {
                    const name = item.windowType;
                    if (!acc[name]) acc[name] = { windowType: name, price: 0 };
                    acc[name].price += item.price;
                    return acc;
                  }, {});
                  return Object.values(byType).map(row => (
                    <div key={row.windowType} className="flex justify-between items-center text-sm">
                      <span className="text-black">{row.windowType}</span>
                      <span className="text-black font-semibold">
                        ${row.price.toFixed(2)}
                      </span>
                    </div>
                  ));
                })()}
                <div className="border-t border-gray-300 pt-2 mt-4">
                  <div className="flex justify-between items-center text-lg font-bold">
                    <span className="text-black">Total Amount</span>
                    <span className="text-black">
                      ${quote.totals.total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes (Feature #5) */}
          {quote.notes && quote.notes.trim() !== '' && (
            <div className="mt-6 pt-4 border-t border-gray-300">
              <h3 className="text-sm font-semibold text-gray-600 mb-2">Notes</h3>
              <p className="text-black text-sm whitespace-pre-wrap">{quote.notes}</p>
            </div>
          )}

          {/* Prices used for this quote (Feature #2.5) - Grouped by structure */}
          {(() => {
            const structures = quote.structures || (quote.lineItems ? [{ name: 'Main', lineItems: quote.lineItems }] : []);
            
            // Calculate prices per structure
            const structurePrices = structures.map(structure => {
              const lineItems = structure.lineItems || [];
              const windowTypeData = {};
              
              lineItems.forEach(item => {
                if (!item.windowType) return;
                
                const count = item.totalCount || item.quantity || 0;
                if (count === 0) return;
                
                // Calculate price per unit: use override if present, otherwise calculate from price/totalCount
                let pricePerUnit;
                if (item.pricePerUnitOverride != null) {
                  pricePerUnit = item.pricePerUnitOverride;
                } else {
                  // Calculate backwards from total price
                  pricePerUnit = count > 0 ? (item.price || 0) / count : 0;
                }
                
                if (!windowTypeData[item.windowType]) {
                  windowTypeData[item.windowType] = {
                    count: 0,
                    pricePerUnit: pricePerUnit,
                    prices: [] // Track all prices to detect variations
                  };
                }
                windowTypeData[item.windowType].count += count;
                windowTypeData[item.windowType].prices.push(pricePerUnit);
              });
              
              // Normalize prices - if all items of same type have same price, use that
              // Otherwise, if there are variations, we'll use the first one (shouldn't happen in practice)
              Object.keys(windowTypeData).forEach(windowType => {
                const data = windowTypeData[windowType];
                const uniquePrices = [...new Set(data.prices.map(p => p.toFixed(2)))];
                if (uniquePrices.length === 1) {
                  data.pricePerUnit = data.prices[0];
                } else {
                  // Multiple prices - use first (this shouldn't happen if price override applies to all)
                  data.pricePerUnit = data.prices[0];
                }
                delete data.prices; // Clean up
              });
              
              return {
                name: structure.name,
                windowTypes: windowTypeData
              };
            }).filter(s => Object.keys(s.windowTypes).length > 0);
            
            if (structurePrices.length === 0) return null;
            
            return (
              <div className="mt-6 pt-4 border-t border-gray-300">
                <h3 className="text-sm font-semibold text-gray-600 mb-3">Prices used for this quote</h3>
                <div className="space-y-4">
                  {structurePrices.map((structure, idx) => (
                    <div key={idx} className="space-y-1">
                      <h4 className="text-xs font-semibold text-gray-500 mb-1">{structure.name}</h4>
                      {Object.entries(structure.windowTypes)
                        .sort((a, b) => a[0].localeCompare(b[0]))
                        .map(([windowTypeName, data]) => (
                          <div key={windowTypeName} className="flex justify-between text-sm ml-2">
                            <span className="text-gray-700">{windowTypeName} - {data.count}</span>
                            <span className="text-gray-600">${Number(data.pricePerUnit).toFixed(2)} per unit</span>
                          </div>
                        ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Jobber Success Toast */}
      {showJobberSuccessToast && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 animate-slide-up">
          <div className="bg-secondary border border-success rounded-xl p-4 shadow-lg flex items-center gap-3">
            <div className="w-5 h-5 rounded-full bg-success flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span className="text-primary font-medium">Quote sent to Jobber successfully</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteDetail;
